import { useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { articles, type ArticleData } from "@/mocks/articles";

function generateBreadcrumbSchema(article: ArticleData) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Startseite", item: "https://oopbuyspread.forum/" },
      { "@type": "ListItem", position: 2, name: article.title, item: `https://oopbuyspread.forum/artikel/${article.slug}` },
    ],
  };
}

function generateArticleSchema(article: ArticleData) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.description,
    image: article.imageUrl,
    datePublished: "2026-07-01",
    dateModified: "2026-07-15",
    author: { "@type": "Organization", name: "OOPBuy Spreadsheet Redaktion" },
    publisher: { "@type": "Organization", name: "OOPBuy Spreadsheet Deutschland" },
    mainEntityOfPage: { "@type": "WebPage", "@id": `https://oopbuyspread.forum/artikel/${article.slug}` },
  };
}

function generateFaqSchema(faqs: ArticleData["faqs"]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({ "@type": "Question", name: faq.q, acceptedAnswer: { "@type": "Answer", text: faq.a } })),
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-20 text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-500 mb-6">
            <i className="ri-error-warning-line text-2xl"></i>
          </div>
          <h1 className="text-2xl font-heading text-foreground-900 mb-3">Artikel nicht gefunden</h1>
          <p className="text-foreground-600 text-sm mb-6">Der gesuchte Artikel existiert leider nicht. Hier geht es zurück zur Startseite.</p>
          <button onClick={() => navigate("/")} className="inline-block bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-full text-sm font-semibold transition-colors cursor-pointer">Zur Startseite</button>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(generateBreadcrumbSchema(article)) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(generateArticleSchema(article)) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(generateFaqSchema(article.faqs)) }} />

      <Navbar />

      <article className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6 flex-wrap" aria-label="Breadcrumb">
          <button onClick={() => navigate("/")} className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">Startseite</button>
          <span>/</span>
          <span className="text-foreground-700 font-medium truncate max-w-[200px] md:max-w-md">{article.title}</span>
        </nav>

        <span className="inline-block bg-accent-100 text-accent-700 text-xs font-semibold px-3 py-1 rounded-full mb-4">{article.badge}</span>

        <h1 className="text-2xl md:text-4xl font-heading text-foreground-900 mb-4 leading-tight">{article.title}</h1>

        <div className="flex flex-wrap items-center gap-3 text-foreground-500 text-xs mb-8">
          <span>Letzte Aktualisierung: Juli 2026</span>
          <span>·</span>
          <span>Lesezeit: {article.readTime}</span>
          <span>·</span>
          <span>Von der OOPBuy Spreadsheet Redaktion</span>
        </div>

        <div className="w-full h-[300px] md:h-[400px] rounded-2xl overflow-hidden mb-8">
          <img src={article.imageUrl} alt={article.title} className="w-full h-full object-cover object-top" />
        </div>

        {article.sections.map((section, sIdx) => {
          if (section.type === "intro" && section.paragraphs) {
            return <p key={sIdx} className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6" dangerouslySetInnerHTML={{ __html: section.paragraphs[0] }} />;
          }
          if (section.type === "cta") {
            return (
              <div key={sIdx} className="bg-primary-500 text-background-50 rounded-2xl p-6 md:p-8 mb-8 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                  <p className="font-semibold text-base md:text-lg mb-1">Bereit loszulegen?</p>
                  <p className="text-background-50/80 text-xs md:text-sm">Entdecke das OOPBuy Spreadsheet jetzt direkt</p>
                </div>
                <a href={section.ctaUrl ?? "https://kaspreadsheet.com"} target="_blank" rel="noopener nofollow" className="bg-background-50 text-primary-500 px-6 py-3 rounded-full text-sm font-semibold hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">{section.ctaText ?? "OOPBuy Spreadsheet öffnen"}</a>
              </div>
            );
          }
          if (section.type === "h2") {
            return <h2 key={sIdx} className="text-xl md:text-2xl font-heading text-foreground-900 mt-10 mb-4">{section.heading}</h2>;
          }
          if (section.type === "paragraph" && section.paragraphs) {
            return section.paragraphs.map((p, pIdx) => <p key={`${sIdx}-${pIdx}`} className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: p }} />);
          }
          if (section.type === "benefit_cards" && section.cards) {
            return (
              <div key={sIdx} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {section.cards.map((card, cIdx) => (
                  <div key={cIdx} className="bg-background-100 border border-background-200/70 rounded-xl p-4 md:p-5">
                    <h3 className="font-heading text-foreground-900 text-base mb-2">{card.title}</h3>
                    <p className="text-foreground-600 text-xs md:text-sm leading-relaxed">{card.desc}</p>
                  </div>
                ))}
              </div>
            );
          }
          if (section.type === "comparison_table" && section.tableHeaders && section.tableRows) {
            return (
              <div key={sIdx} className="bg-background-50 border border-background-200/70 rounded-2xl p-6 md:p-8 overflow-x-auto mb-8">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-background-200/70">
                      {section.tableHeaders.map((h, hIdx) => (
                        <th key={hIdx} className={`text-left py-4 pr-4 ${hIdx === 1 ? "text-primary-600" : "text-foreground-700"} font-semibold whitespace-nowrap`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {section.tableRows.map((row, rIdx) => (
                      <tr key={rIdx} className={rIdx % 2 === 0 ? "bg-background-50" : "bg-background-100/50"}>
                        {row.map((cell, cIdx) => (
                          <td key={cIdx} className={`py-3.5 pr-4 ${cIdx === 1 ? "text-primary-600 font-semibold" : cIdx === 0 ? "text-foreground-800 font-medium" : "text-foreground-500"} whitespace-nowrap`}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }
          if (section.type === "steps" && section.steps) {
            return (
              <div key={sIdx} className="space-y-6 mb-8">
                {section.steps.map((item) => (
                  <div key={item.step} className="flex gap-4">
                    <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 font-bold text-sm flex-shrink-0">{item.step}</div>
                    <div>
                      <h3 className="font-heading text-foreground-900 text-base md:text-lg mb-1">{item.title}</h3>
                      <p className="text-foreground-600 text-xs md:text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            );
          }
          if (section.type === "tips" && section.tips) {
            return (
              <div key={sIdx} className="space-y-3 mb-8">
                {section.tips.map((tip, tIdx) => (
                  <div key={tIdx} className="flex items-start gap-3 text-foreground-600 text-sm">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-check-line text-accent-500 text-sm"></i></div>
                    <span><strong>{tIdx + 1}.</strong> {tip}</span>
                  </div>
                ))}
              </div>
            );
          }
          return null;
        })}

        {article.faqs.length > 0 && (
          <>
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mt-10 mb-4">Häufig gestellte Fragen (FAQ)</h2>
            <div className="space-y-3 mb-8">
              {article.faqs.map((faq, idx) => (
                <details key={idx} className="group bg-background-100 border border-background-200/70 rounded-xl p-4 md:p-5 cursor-pointer">
                  <summary className="flex items-center justify-between text-foreground-900 font-medium text-sm md:text-base">
                    <span>{faq.q}</span>
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 ml-2">
                      <i className="ri-arrow-down-s-line group-open:hidden"></i>
                      <i className="ri-arrow-up-s-line hidden group-open:block"></i>
                    </div>
                  </summary>
                  <p className="text-foreground-600 text-xs md:text-sm leading-relaxed mt-3">{faq.a}</p>
                </details>
              ))}
            </div>
          </>
        )}

        <div className="bg-background-100 border border-background-200/70 rounded-2xl p-6 md:p-8 mb-8">
          <h3 className="font-heading text-foreground-900 text-lg mb-4">Weiterführende Links</h3>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => navigate("/")} className="text-primary-500 hover:text-primary-600 text-sm underline cursor-pointer whitespace-nowrap">← Zurück zur Startseite</button>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-accent-600 hover:text-accent-700 text-sm underline whitespace-nowrap">OOPBuy Spreadsheet öffnen</a>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}